import { Button } from '@any-ui-react/core';
import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react';
import { ModalFooter } from '@/common/components/shared/modal-footer/ModalFooter';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/common/components/shared/table/constants';
import { TablePagination } from '@/common/components/shared/table/pagination/TablePagination';
import {
  type ListEmployeesFilterInput,
  useListDepartmentsQuery,
  useListEmployeesForSearchPopupQuery,
} from '@/common/graphql';
import type { EmployeeSearchFilterType, TableSelection } from '@/common/types';
import { ApiUtils, toIdArray } from '@/common/utils';

import { EmployeeSearchFilters } from './EmployeeSearchFilters';
import {
  buildEmployeeSearchFilter,
  deriveInitialFilters,
  findDepartmentsByExactNames,
  mergeIncludeIds,
} from './EmployeeSearchForm.utils';
import { type EmployeeSearchResultRow, EmployeeSearchTable } from './EmployeeSearchTable';
import type { EmployeeSearchProps } from './props';

interface Props extends EmployeeSearchProps {
  baseVariables: ListEmployeesFilterInput;
  mode: 'single' | 'multi';
  /** Currently-selected id(s) to seed/pre-check in the popup - a single id string in `mode:
   * 'single'`, a string array in `mode: 'multi'` (empty/null/undefined when nothing is selected). */
  value: string | string[] | null | undefined;
  /**
   * Called with the confirmed selection - a single row in `mode: 'single'` (row click confirms immediately),
   * an array of rows in `mode: 'multi'` (confirmed via the "Select" button).
   */
  onConfirm: (selected: EmployeeSearchResultRow | EmployeeSearchResultRow[]) => void;
  onCancel: () => void;
}

/**
 * Seeds the initial multi-select `selection` from already-assigned employees (`value`), fetched
 * via a dedicated, isolated query - scoped ONLY to `includeIds` - instead of merging `includeIds`
 * into the main list query. Merging it into the main query collapses the result set to JUST those
 * ids whenever the main query's other filters resolve to "no restriction" (jOOQ's `NoCondition` is
 * absorbed by `.or()` on either side, so `noCondition().or(id IN (...))` becomes `id IN (...)`
 * alone, not "everyone, plus these"). Keeping this fetch fully separate means the main list query
 * never carries `includeIds` and therefore never triggers that collapse (research.md #10, FR-013).
 *
 * Returns `seedStatus` so the caller can disable its "Select" button until seeding resolves -
 * confirming before the seed completes would otherwise silently drop the pre-existing selection.
 * Guarded by `seedStatus` (not a ref) so this never re-fires and overrides selections the user
 * makes afterward, including unchecking a previously-assigned employee.
 *
 * On a query error, `seedStatus` still resolves (with a toast) rather than staying `'pending'`
 * forever - otherwise a transient network/GraphQL error would permanently disable the "Select"
 * button with no recovery path.
 */
const useSeedInitialSelection = ({
  mode,
  value,
  baseVariables,
  setSelection,
}: {
  mode: 'single' | 'multi';
  value: string | string[] | null | undefined;
  baseVariables: ListEmployeesFilterInput;
  setSelection: Dispatch<SetStateAction<TableSelection<EmployeeSearchResultRow>>>;
}) => {
  const selectedIds = toIdArray(value);
  const shouldSeed = mode === 'multi' && selectedIds.length > 0;
  const [seedStatus, setSeedStatus] = useState<'pending' | 'resolved'>(shouldSeed ? 'pending' : 'resolved');

  const { data: selectedEmployeesData, error: seedError } = useListEmployeesForSearchPopupQuery({
    variables: {
      pageNumber: 1,
      pageSize: selectedIds.length,
      filter: { includeIds: mergeIncludeIds(value, baseVariables) },
      sort: [],
    },
    skip: !shouldSeed,
  });

  useEffect(() => {
    if (seedStatus !== 'pending') return;

    if (seedError) {
      ApiUtils.handleApiError(seedError);
      setSeedStatus('resolved');
      return;
    }

    const alreadyAssigned = selectedEmployeesData?.listEmployees.items;
    if (!alreadyAssigned) return; // seed query still loading

    setSeedStatus('resolved');
    if (alreadyAssigned.length > 0) {
      setSelection(prev => ({ ...prev, items: [...prev.items, ...alreadyAssigned] }));
    }
  }, [selectedEmployeesData, seedError, seedStatus, setSelection]);

  return { seedStatus };
};

/**
 * Auto-selects the Department filter to `defaultDepartmentName` once Departments finish loading -
 * ref-guarded so it applies at most once and never fights a selection the user makes afterward
 * (including clearing the Department filter again). Skips entirely when `defaultDepartmentName` is
 * unset or `departmentIds` is already non-empty (never overrides a selection already derived from
 * `baseVariables`). Resolves even when no Department matches (or the query errors) rather than
 * retrying, since a permanent no-op is the correct behavior per FR - "if not, do nothing".
 */
const useApplyDefaultDepartment = ({
  defaultDepartmentName,
  departmentIds,
  onApply,
}: {
  defaultDepartmentName: string | string[] | undefined;
  departmentIds: string[];
  onApply: (departmentIds: string[]) => void;
}) => {
  const appliedRef = useRef(false);
  const names = toIdArray(defaultDepartmentName);
  const shouldApply = names.length > 0 && departmentIds.length === 0 && !appliedRef.current;

  const { data, loading } = useListDepartmentsQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      keyword: null,
      enabled: null,
      hasEmployees: true,
      excludeIds: null,
      companyId: null,
      businessUnitId: null,
    },
    fetchPolicy: 'cache-first',
    skip: !shouldApply,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: `names` is derived fresh each render; use its joined value as the stable dependency instead of the array reference
  useEffect(() => {
    if (!shouldApply || loading) return;
    appliedRef.current = true;

    const matchIds = findDepartmentsByExactNames(data?.listDepartments.items ?? [], names);
    if (matchIds.length > 0) onApply(matchIds);
  }, [shouldApply, loading, data, names.join(''), onApply]);
};

/**
 * Auto-selects a filter (Company or Country) to `defaultId` - simpler than
 * `useApplyDefaultDepartment` since the id(s) are already resolved by the caller (no query
 * needed). Ref-guarded so it applies at most once and never fights a later user change.
 */
const useApplyDefaultIdFilter = ({
  defaultId,
  currentIds,
  onApply,
}: {
  defaultId: string | string[] | undefined;
  currentIds: string[];
  onApply: (ids: string[]) => void;
}) => {
  const appliedRef = useRef(false);
  const ids = toIdArray(defaultId);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `ids` is derived fresh each render; use its joined value as the stable dependency instead of the array reference
  useEffect(() => {
    if (appliedRef.current || ids.length === 0 || currentIds.length > 0) return;
    appliedRef.current = true;
    onApply(ids);
  }, [ids.join(''), currentIds.length, onApply]);
};

/**
 * Resolves department name(s) to id(s), usable from outside EmployeeSearchForm/Popup entirely -
 * e.g. to build a hard-restricted `variables.departmentIds` allowlist for EmployeeSelectSingle/
 * Multiple (FR-017), which - via `toPopupBaseVariables`/`deriveInitialFilters`/
 * `createAllowlistFilter` - also forces and disables the popup's own Department filter, unlike
 * `defaultDepartmentName`'s pre-fill-only, user-clearable behavior.
 */
export const useDepartmentIdsByExactNames = (names: string[]): { departmentIds: string[]; loading: boolean } => {
  const { data, loading } = useListDepartmentsQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      keyword: null,
      enabled: null,
      hasEmployees: true,
      excludeIds: null,
      companyId: null,
      businessUnitId: null,
    },
    fetchPolicy: 'cache-first',
    skip: names.length === 0,
  });

  return { departmentIds: findDepartmentsByExactNames(data?.listDepartments.items ?? [], names), loading };
};

export const EmployeeSearchForm = ({
  mode,
  baseVariables,
  value,
  sort,
  onConfirm,
  onCancel,
  defaultDepartmentName,
  defaultCompanyId,
  defaultCountryId,
  searchFilters,
}: Props) => {
  const [filters, setFilters] = useState<EmployeeSearchFilterType>(() => deriveInitialFilters(baseVariables));
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState<TableSelection<EmployeeSearchResultRow>>({ items: [], allPages: false });
  const { seedStatus } = useSeedInitialSelection({ mode, value, baseVariables, setSelection });

  const selectedIds = toIdArray(value);

  const updateFilters = (patch: Partial<EmployeeSearchFilterType>) => {
    setFilters(prev => ({ ...prev, ...patch }));
    setPage(1);
  };

  useApplyDefaultIdFilter({
    defaultId: defaultCountryId,
    currentIds: filters.countryIds,
    onApply: countryIds => updateFilters({ countryIds }),
  });

  useApplyDefaultIdFilter({
    defaultId: defaultCompanyId,
    currentIds: filters.companyIds,
    onApply: companyIds => updateFilters({ companyIds }),
  });

  useApplyDefaultDepartment({
    defaultDepartmentName,
    departmentIds: filters.departmentIds,
    onApply: departmentIds => updateFilters({ departmentIds }),
  });

  const { data, loading } = useListEmployeesForSearchPopupQuery({
    variables: {
      pageNumber: page,
      pageSize: DEFAULT_TABLE_PAGE_SIZE,
      filter: buildEmployeeSearchFilter(baseVariables, filters),
      sort: sort ?? [],
    },
  });

  const items = data?.listEmployees.items ?? [];
  const totalCount = data?.listEmployees.totalCount ?? 0;

  const handleConfirm = () => onConfirm(selection.items);

  return (
    <div>
      <EmployeeSearchFilters
        baseVariables={baseVariables}
        filters={filters}
        searchFilters={searchFilters}
        onChange={updateFilters}
      />
      <div className="-mx-6">
        <EmployeeSearchTable
          items={items}
          loading={loading}
          page={page}
          selectedIds={selectedIds}
          selection={mode === 'multi' ? selection : undefined}
          total={totalCount}
          onRowClick={mode === 'single' ? row => onConfirm(row) : undefined}
          onSelectionChange={mode === 'multi' ? setSelection : undefined}
        />
      </div>
      <TablePagination count={totalCount} rowsPerPage={DEFAULT_TABLE_PAGE_SIZE} value={page} onChange={setPage} />
      <ModalFooter>
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        {mode === 'multi' && (
          <Button disabled={selection.items.length === 0 || seedStatus === 'pending'} onClick={handleConfirm}>
            Select
          </Button>
        )}
      </ModalFooter>
    </div>
  );
};
